import { isQualifiedEvidence, type ExerciseAttempt, type LearningEvidenceKind } from '@/entities/attempt'
import type { SkillDimension } from '@/types'
import type {
  AbilityReportItem,
  KnowledgePointReportItem,
  LearningReport,
  LearningReportOutcomes,
  LearningReportRate,
  LearningReportRecommendation,
} from './types'

const ABILITIES: SkillDimension[] = ['listening', 'speaking', 'reading', 'writing']

interface CompletedAssessmentReference {
  kind?: 'unit-assessment' | 'certification'
  sessionId?: string
}

const ABILITY_LABELS: Record<SkillDimension, string> = {
  listening: '听力',
  speaking: '口语',
  reading: '阅读',
  writing: '写作',
}

function round(value: number): number {
  return Math.round(value * 10) / 10
}

function percentage(correct: number, total: number): number {
  return total === 0 ? 0 : round((correct / total) * 100)
}

const LEARNER_FACING_KNOWLEDGE_LABELS: Record<string, string> = {
  'detective-adventure': '侦探阅读任务',
  'quest-adventure': '任务阅读',
  'drag-spelling': '单词拼写',
  practice: '专项练习',
  assessment: '能力检测',
  listening: '听力理解',
  speaking: '口语表达',
  reading: '阅读理解',
  writing: '写作表达',
  vocabulary: '词汇运用',
  grammar: '语法运用',
  sentence: '句型运用',
  phonics: '自然拼读',
}

function learnerFacingKnowledgeLabel(value?: string): string | undefined {
  if (!value) return undefined
  const normalized = value.trim().toLowerCase()
  const segments = normalized.split(':')
  if (LEARNER_FACING_KNOWLEDGE_LABELS[normalized]) {
    return LEARNER_FACING_KNOWLEDGE_LABELS[normalized]
  }

  const dimensions = ['listening', 'speaking', 'reading', 'writing'] as const
  const knownTopic = [...segments].reverse().find((segment) => (
    LEARNER_FACING_KNOWLEDGE_LABELS[segment]
    && !dimensions.includes(segment as typeof dimensions[number])
  ))
  if (knownTopic) return LEARNER_FACING_KNOWLEDGE_LABELS[knownTopic]

  const dimension = dimensions.find((ability) => segments.includes(ability))
  if (dimension) return `${ABILITY_LABELS[dimension]}知识点`
  if (/^[a-z0-9:_-]+$/i.test(value)) return '教材知识点'
  return value
}

function createRate(achievedCount: number, eligibleCount: number): LearningReportRate {
  return {
    achievedCount,
    eligibleCount,
    rate: eligibleCount === 0 ? null : percentage(achievedCount, eligibleCount),
  }
}

function attemptScore(attempt: ExerciseAttempt): number {
  return Number.isFinite(attempt.score)
    ? Math.max(0, Math.min(100, attempt.score ?? 0))
    : attempt.correct ? 100 : 0
}

function evidenceKind(attempt: ExerciseAttempt): LearningEvidenceKind {
  if (attempt.evidenceKind) return attempt.evidenceKind
  return attempt.source === 'assessment' ? 'summative' : 'formative'
}

function buildOverview(attempts: ExerciseAttempt[]): LearningReport['overview'] {
  const correctCount = attempts.filter((attempt) => attempt.correct).length
  return {
    attemptCount: attempts.length,
    correctCount,
    incorrectCount: attempts.length - correctCount,
    accuracy: attempts.length === 0
      ? 0
      : round(attempts.reduce((sum, attempt) => sum + attemptScore(attempt), 0) / attempts.length),
    averageDurationMs: average(
      attempts.reduce((sum, attempt) => sum + attempt.durationMs, 0),
      attempts.length,
    ),
    totalHintCount: attempts.reduce((sum, attempt) => sum + attempt.hintCount, 0),
    totalRetryCount: attempts.reduce((sum, attempt) => sum + attempt.retryCount, 0),
  }
}

function average(total: number, count: number): number {
  return count === 0 ? 0 : Math.round(total / count)
}

function sortByCompletedAt(attempts: ExerciseAttempt[]): ExerciseAttempt[] {
  return [...attempts].sort(
    (left, right) => new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime(),
  )
}

function learningItemKey(attempt: ExerciseAttempt): string {
  return [
    evidenceKind(attempt),
    attempt.source,
    attempt.stageId ?? '',
    attempt.unitId ?? '',
    attempt.taskId ?? attempt.lessonId ?? '',
    attempt.exerciseId,
  ].join(':')
}

function groupAttempts(
  attempts: readonly ExerciseAttempt[],
  keyForAttempt: (attempt: ExerciseAttempt) => string,
): ExerciseAttempt[][] {
  const groups = new Map<string, ExerciseAttempt[]>()
  for (const attempt of attempts) {
    const key = keyForAttempt(attempt)
    groups.set(key, [...(groups.get(key) ?? []), attempt])
  }
  return [...groups.values()].map((group) => sortByCompletedAt(group))
}

function certificationGroupKey(attempt: ExerciseAttempt): string {
  return [
    attempt.stageId ?? '',
    attempt.unitId ?? '',
    attempt.knowledgePointId ?? attempt.subCategory ?? '',
    attempt.assessmentSessionId ?? '',
  ].join(':')
}

function certificationThreshold(attempt: ExerciseAttempt): number {
  if (attempt.layer === 'A') return 90
  if (attempt.layer === 'C') return 70
  return 80
}

function buildLearningOutcomes(attempts: ExerciseAttempt[]): LearningReportOutcomes {
  const independentAttempts = attempts.filter((attempt) => {
    const kind = evidenceKind(attempt)
    return kind === 'formative' || kind === 'summative'
  })
  const learningItems = groupAttempts(independentAttempts, learningItemKey)
  const firstCorrectCount = learningItems.filter((group) => group[0]?.correct).length
  const firstWrongItems = learningItems.filter((group) => !group[0]?.correct)
  const correctedCount = firstWrongItems.filter((group) => group.slice(1).some((attempt) => attempt.correct)).length

  const certificationGroups = groupAttempts(
    attempts.filter((attempt) => (
      evidenceKind(attempt) === 'certification'
      && Boolean(attempt.assessmentSessionId)
      && Boolean(attempt.contentFingerprint)
      && isQualifiedEvidence(attempt)
    )),
    certificationGroupKey,
  ).flatMap((group) => {
    const firstByQuestion = new Map<string, ExerciseAttempt>()
    for (const attempt of group) {
      const fingerprint = attempt.contentFingerprint as string
      if (!firstByQuestion.has(fingerprint)) firstByQuestion.set(fingerprint, attempt)
    }
    const firstAttempts = [...firstByQuestion.values()]
    return group.length === 5 && firstAttempts.length === 5 ? [firstAttempts] : []
  })
  const masteredCertificationGroups = certificationGroups.filter((group) => {
    const score = percentage(group.filter((attempt) => attempt.correct).length, group.length)
    return score >= certificationThreshold(group[0])
  }).length

  return {
    firstAttemptAccuracy: createRate(firstCorrectCount, learningItems.length),
    correctionRate: createRate(correctedCount, firstWrongItems.length),
    independentCertificationMasteryRate: createRate(
      masteredCertificationGroups,
      certificationGroups.length,
    ),
  }
}

function buildAbilityReport(attempts: ExerciseAttempt[]): AbilityReportItem[] {
  return ABILITIES.map((ability) => {
    const matching = attempts.filter((attempt) => attempt.ability === ability)
    const correctCount = matching.filter((attempt) => attempt.correct).length

    return {
      ability,
      attemptCount: matching.length,
      correctCount,
      accuracy: matching.length === 0
        ? 0
        : round(matching.reduce((sum, attempt) => sum + attemptScore(attempt), 0) / matching.length),
      averageDurationMs: average(
        matching.reduce((sum, attempt) => sum + attempt.durationMs, 0),
        matching.length,
      ),
      hintCount: matching.reduce((sum, attempt) => sum + attempt.hintCount, 0),
      retryCount: matching.reduce((sum, attempt) => sum + attempt.retryCount, 0),
    }
  })
}

function buildKnowledgePointReport(attempts: ExerciseAttempt[]): KnowledgePointReportItem[] {
  const groups = new Map<string, ExerciseAttempt[]>()

  attempts.forEach((attempt) => {
    if (!attempt.knowledgePointId) return
    const group = groups.get(attempt.knowledgePointId) ?? []
    group.push(attempt)
    groups.set(attempt.knowledgePointId, group)
  })

  return Array.from(groups.entries())
    .map(([knowledgePointId, entries]) => {
      const ordered = sortByCompletedAt(entries)
      const latest = ordered[ordered.length - 1]
      const correctCount = entries.filter((attempt) => attempt.correct).length

      return {
        knowledgePointId: learnerFacingKnowledgeLabel(knowledgePointId) ?? knowledgePointId,
        category: learnerFacingKnowledgeLabel(latest.category),
        subCategory: learnerFacingKnowledgeLabel(latest.subCategory),
        attemptCount: entries.length,
        correctCount,
        accuracy: round(entries.reduce((sum, attempt) => sum + attemptScore(attempt), 0) / entries.length),
        averageDurationMs: average(
          entries.reduce((sum, attempt) => sum + attempt.durationMs, 0),
          entries.length,
        ),
        hintCount: entries.reduce((sum, attempt) => sum + attempt.hintCount, 0),
        retryCount: entries.reduce((sum, attempt) => sum + attempt.retryCount, 0),
        lastAttemptAt: latest.completedAt,
      }
    })
    .sort((left, right) => {
      if (left.accuracy !== right.accuracy) return left.accuracy - right.accuracy
      return right.attemptCount - left.attemptCount
    })
}

function createRecommendations(
  abilities: AbilityReportItem[],
  knowledgePoints: KnowledgePointReportItem[],
  overviewAccuracy: number,
): LearningReportRecommendation[] {
  const recommendations: LearningReportRecommendation[] = []

  const weakAbilities = abilities
    .filter((item) => item.attemptCount >= 2 && item.accuracy < 70)
    .sort((left, right) => left.accuracy - right.accuracy)
    .slice(0, 2)

  weakAbilities.forEach((item) => {
    recommendations.push({
      id: `ability-${item.ability}`,
      priority: item.accuracy < 50 ? 'high' : 'medium',
      title: `加强${ABILITY_LABELS[item.ability]}训练`,
      reason: `${ABILITY_LABELS[item.ability]}正确率为 ${item.accuracy}%（${item.correctCount}/${item.attemptCount}）。`,
      action: `下一阶段增加短时、多轮的${ABILITY_LABELS[item.ability]}练习，并在答错后安排同类变式题。`,
      ability: item.ability,
    })
  })

  knowledgePoints
    .filter((item) => item.attemptCount >= 2 && item.accuracy < 70)
    .slice(0, 3)
    .forEach((item) => {
      recommendations.push({
        id: `knowledge-${item.knowledgePointId}`,
        priority: item.accuracy < 50 ? 'high' : 'medium',
        title: `复习知识点：${item.subCategory || item.category || item.knowledgePointId}`,
        reason: `该知识点正确率为 ${item.accuracy}%，共作答 ${item.attemptCount} 次。`,
        action: '先回看示例，再完成一组由易到难的巩固练习，并在次日进行一次短复习。',
        knowledgePointId: item.knowledgePointId,
      })
    })

  if (recommendations.length === 0 && overviewAccuracy >= 80) {
    recommendations.push({
      id: 'maintain-progress',
      priority: 'low',
      title: '保持当前学习节奏',
      reason: `当前综合正确率为 ${overviewAccuracy}%，整体表现稳定。`,
      action: '继续完成当前学习计划，并适当增加综合运用和口语输出任务。',
    })
  }

  if (recommendations.length === 0) {
    recommendations.push({
      id: 'collect-more-data',
      priority: 'low',
      title: '继续完成更多练习',
      reason: '当前有效作答样本较少，暂时不足以形成稳定的薄弱项判断。',
      action: '继续完成课堂、课程练习和单元检测，系统会随作答增加自动更新报告。',
    })
  }

  return recommendations.slice(0, 5)
}

export function generateLearningReport(
  attempts: ExerciseAttempt[],
  studentId?: string,
  options: { stageId?: string; assessmentRecords?: readonly CompletedAssessmentReference[] } = {},
): LearningReport | null {
  const targetStudentId = studentId ?? attempts[attempts.length - 1]?.studentId
  if (!targetStudentId) return null

  const allStudentAttempts = attempts.filter((attempt) => (
    attempt.studentId === targetStudentId
    && isQualifiedEvidence(attempt)
  ))
  const stageAttempts = options.stageId
    ? allStudentAttempts.filter((attempt) => attempt.stageId === options.stageId)
    : allStudentAttempts
  const completedSummativeSessionIds = new Set((options.assessmentRecords ?? [])
    .filter((record) => record.kind !== 'certification' && record.sessionId)
    .map((record) => record.sessionId as string))
  const eligibleStageAttempts = options.assessmentRecords
    ? stageAttempts.filter((attempt) => (
        evidenceKind(attempt) !== 'summative'
        || Boolean(attempt.assessmentSessionId && completedSummativeSessionIds.has(attempt.assessmentSessionId))
      ))
    : stageAttempts
  const studentAttempts = sortByCompletedAt(
    eligibleStageAttempts.filter((attempt) => evidenceKind(attempt) !== 'remediation-training'),
  )

  const overview = buildOverview(studentAttempts)
  const overviewAccuracy = overview.accuracy
  const abilities = buildAbilityReport(studentAttempts)
  const knowledgePoints = buildKnowledgePointReport(studentAttempts)
  const evidenceLabels: Record<LearningEvidenceKind, string> = {
    formative: '阶段训练',
    summative: '正式评测',
    'remediation-training': 'AI补救训练',
    certification: '独立认证',
  }
  const kinds: LearningEvidenceKind[] = ['formative', 'summative', 'remediation-training', 'certification']
  const evidenceBreakdown = kinds.map((kind) => {
    const matching = eligibleStageAttempts.filter((attempt) => evidenceKind(attempt) === kind)
    return { kind, label: evidenceLabels[kind], overview: buildOverview(matching) }
  })
  const officialAttempts = eligibleStageAttempts.filter((attempt) => evidenceKind(attempt) === 'summative')

  const measuredAbilities = abilities.filter((item) => item.attemptCount >= 2)
  const strongestAbilities = measuredAbilities
    .filter((item) => item.accuracy >= 80)
    .sort((left, right) => right.accuracy - left.accuracy)
    .slice(0, 2)
  const weakestAbilities = measuredAbilities
    .filter((item) => item.accuracy < 70)
    .sort((left, right) => left.accuracy - right.accuracy)
    .slice(0, 2)

  const strongKnowledgePoints = knowledgePoints
    .filter((item) => item.attemptCount >= 2 && item.accuracy >= 80)
    .sort((left, right) => right.accuracy - left.accuracy)
    .slice(0, 2)
  const weakKnowledgePoints = knowledgePoints
    .filter((item) => item.attemptCount >= 2 && item.accuracy < 70)
    .slice(0, 3)

  const strengths = [
    ...strongestAbilities.map(
      (item) => `${ABILITY_LABELS[item.ability]}表现稳定（正确率 ${item.accuracy}%）`,
    ),
    ...strongKnowledgePoints.map(
      (item) => `${item.subCategory || item.category || item.knowledgePointId}掌握较好（正确率 ${item.accuracy}%）`,
    ),
  ].slice(0, 4)

  const weaknesses = [
    ...weakestAbilities.map(
      (item) => `${ABILITY_LABELS[item.ability]}需要加强（正确率 ${item.accuracy}%）`,
    ),
    ...weakKnowledgePoints.map(
      (item) => `${item.subCategory || item.category || item.knowledgePointId}需要巩固（正确率 ${item.accuracy}%）`,
    ),
  ].slice(0, 5)

  const generatedAt = new Date().toISOString()

  return {
    id: `report-${targetStudentId}-${generatedAt}`,
    studentId: targetStudentId,
    generatedAt,
    periodStart: studentAttempts[0]?.completedAt ?? null,
    periodEnd: studentAttempts[studentAttempts.length - 1]?.completedAt ?? null,
    stageId: options.stageId,
    overview,
    officialOverview: officialAttempts.length > 0 ? buildOverview(officialAttempts) : null,
    outcomes: buildLearningOutcomes(eligibleStageAttempts),
    evidenceBreakdown,
    abilities,
    knowledgePoints,
    strengths,
    weaknesses,
    recommendations: createRecommendations(abilities, knowledgePoints, overviewAccuracy),
  }
}
